import type { ChordStats, SessionSummary, TransitionStats } from '@/types/training';
import { foldChordStats, foldTransition, groupSamples } from '@/training/aggregate';
import { getDb } from './db';
import { isValidChordStats, isValidSession, isValidTransition, keepValid } from './validate';

/**
 * The only interface the rest of the app knows about.
 *
 * Everything above this line is storage-agnostic, so adding cloud sync later
 * means writing a second implementation, not rewriting the app.
 */
export interface ProgressRepository {
  saveSession(summary: SessionSummary): Promise<void>;
  recentSessions(limit?: number): Promise<SessionSummary[]>;
  sessionsSince(timestamp: number): Promise<SessionSummary[]>;
  allTransitions(): Promise<TransitionStats[]>;
  getTransition(key: string): Promise<TransitionStats | undefined>;
  allChordStats(): Promise<ChordStats[]>;
  clear(): Promise<void>;
}

class IndexedDbRepository implements ProgressRepository {
  async saveSession(summary: SessionSummary): Promise<void> {
    const db = await getDb();
    const tx = db.transaction(['sessions', 'transitions', 'chordStats'], 'readwrite');

    await tx.objectStore('sessions').put(summary);

    const transitions = tx.objectStore('transitions');
    for (const [key, samples] of groupSamples(summary.transitions)) {
      const existing = await transitions.get(key);
      // A corrupt aggregate is rebuilt from scratch rather than folded into.
      const base = existing && isValidTransition(existing) ? existing : undefined;
      const folded = foldTransition(base, samples);
      if (folded) await transitions.put(folded);
    }

    const chordStats = tx.objectStore('chordStats');
    for (const chordId of summary.chordIds) {
      const existing = await chordStats.get(chordId);
      const base = existing && isValidChordStats(existing) ? existing : undefined;
      await chordStats.put(foldChordStats(base, chordId, summary));
    }

    await tx.done;
  }

  async recentSessions(limit = 30): Promise<SessionSummary[]> {
    const db = await getDb();
    const all = await db.getAllFromIndex('sessions', 'by-date');
    return keepValid(all, isValidSession, 'session').reverse().slice(0, limit);
  }

  async sessionsSince(timestamp: number): Promise<SessionSummary[]> {
    const db = await getDb();
    const all = await db.getAllFromIndex('sessions', 'by-date', IDBKeyRange.lowerBound(timestamp));
    return keepValid(all, isValidSession, 'session').reverse();
  }

  async allTransitions(): Promise<TransitionStats[]> {
    const db = await getDb();
    return keepValid(await db.getAll('transitions'), isValidTransition, 'transition');
  }

  async getTransition(key: string): Promise<TransitionStats | undefined> {
    const db = await getDb();
    const value = await db.get('transitions', key);
    return value && isValidTransition(value) ? value : undefined;
  }

  async allChordStats(): Promise<ChordStats[]> {
    const db = await getDb();
    return keepValid(await db.getAll('chordStats'), isValidChordStats, 'chord');
  }

  async clear(): Promise<void> {
    const db = await getDb();
    const tx = db.transaction(['sessions', 'transitions', 'chordStats'], 'readwrite');
    await Promise.all([
      tx.objectStore('sessions').clear(),
      tx.objectStore('transitions').clear(),
      tx.objectStore('chordStats').clear(),
      tx.done,
    ]);
  }
}

/**
 * In-memory fallback. Used when IndexedDB is unavailable or unusable (Safari
 * private mode, blocked storage, a database written by a newer build) so a
 * session still completes and shows its results — it simply will not survive a
 * reload.
 */
export class MemoryRepository implements ProgressRepository {
  private sessions: SessionSummary[] = [];
  private transitions = new Map<string, TransitionStats>();
  private chords = new Map<string, ChordStats>();

  async saveSession(summary: SessionSummary): Promise<void> {
    this.sessions.push(summary);
    for (const [key, samples] of groupSamples(summary.transitions)) {
      const folded = foldTransition(this.transitions.get(key), samples);
      if (folded) this.transitions.set(key, folded);
    }
    for (const chordId of summary.chordIds) {
      this.chords.set(chordId, foldChordStats(this.chords.get(chordId), chordId, summary));
    }
  }

  async recentSessions(limit = 30): Promise<SessionSummary[]> {
    return [...this.sessions].sort((a, b) => b.startedAt - a.startedAt).slice(0, limit);
  }

  async sessionsSince(timestamp: number): Promise<SessionSummary[]> {
    return (await this.recentSessions(Number.MAX_SAFE_INTEGER)).filter((s) => s.startedAt >= timestamp);
  }

  async allTransitions(): Promise<TransitionStats[]> {
    return [...this.transitions.values()];
  }

  async getTransition(key: string): Promise<TransitionStats | undefined> {
    return this.transitions.get(key);
  }

  async allChordStats(): Promise<ChordStats[]> {
    return [...this.chords.values()];
  }

  async clear(): Promise<void> {
    this.sessions = [];
    this.transitions.clear();
    this.chords.clear();
  }
}

/**
 * Uses IndexedDB, and permanently falls back to memory the first time it proves
 * unusable.
 *
 * Without the latch, a browser that rejects every `open()` would retry — and
 * log — on every single read, and the player would watch a results screen fail
 * to load repeatedly instead of simply losing history they cannot have anyway.
 */
class ResilientRepository implements ProgressRepository {
  private readonly primary = new IndexedDbRepository();
  private readonly fallback = new MemoryRepository();
  private useFallback = typeof indexedDB === 'undefined';

  private async run<T>(operation: (repo: ProgressRepository) => Promise<T>): Promise<T> {
    if (this.useFallback) return operation(this.fallback);
    try {
      return await operation(this.primary);
    } catch (error) {
      console.warn('[storage] IndexedDB unusable, continuing in memory for this session', error);
      this.useFallback = true;
      return operation(this.fallback);
    }
  }

  saveSession(summary: SessionSummary): Promise<void> {
    return this.run((repo) => repo.saveSession(summary));
  }
  recentSessions(limit?: number): Promise<SessionSummary[]> {
    return this.run((repo) => repo.recentSessions(limit));
  }
  sessionsSince(timestamp: number): Promise<SessionSummary[]> {
    return this.run((repo) => repo.sessionsSince(timestamp));
  }
  allTransitions(): Promise<TransitionStats[]> {
    return this.run((repo) => repo.allTransitions());
  }
  getTransition(key: string): Promise<TransitionStats | undefined> {
    return this.run((repo) => repo.getTransition(key));
  }
  allChordStats(): Promise<ChordStats[]> {
    return this.run((repo) => repo.allChordStats());
  }
  clear(): Promise<void> {
    return this.run((repo) => repo.clear());
  }
}

export const progressRepository: ProgressRepository = new ResilientRepository();
export { IndexedDbRepository };
